import { clock } from '@/lib/format';
import { DateTime } from 'luxon';
import { CALENDAR_TZ, type CalendarItem, type CalendarRecurrence } from '@tradeoutlet/shared';

export type CalView = 'month' | 'week' | 'day' | 'agenda';
export const AGENDA_DAYS = 30;

const local = (iso: string) => DateTime.fromISO(iso, { zone: 'utc' }).setZone(CALENDAR_TZ);
const date = (s: string) => DateTime.fromISO(s, { zone: CALENDAR_TZ });

/** Visible range [from, to) and the days shown for a view around an anchor date (weeks start Monday). */
export function viewRange(view: CalView, anchorIso: string): { from: string; to: string; days: string[] } {
  const a = date(anchorIso).startOf('day');
  const start = view === 'month' ? a.startOf('month').startOf('week') : view === 'week' ? a.startOf('week') : a;
  const n = view === 'month' ? 42 : view === 'week' ? 7 : view === 'day' ? 1 : AGENDA_DAYS;
  const days = Array.from({ length: n }, (_, i) => start.plus({ days: i }).toISODate()!);
  return { from: days[0], to: start.plus({ days: n }).toISODate()!, days };
}

export function stepAnchor(view: CalView, anchorIso: string, dir: -1 | 1): string {
  const a = date(anchorIso);
  const d = view === 'month' ? a.plus({ months: dir }) : view === 'week' ? a.plus({ weeks: dir }) : view === 'day' ? a.plus({ days: dir }) : a.plus({ days: dir * AGENDA_DAYS });
  return d.toISODate()!;
}

export function rangeTitle(view: CalView, anchorIso: string): string {
  const a = date(anchorIso);
  if (view === 'month') return a.toFormat('LLLL yyyy');
  if (view === 'day') return a.toFormat('cccc d LLLL yyyy');
  const { days } = viewRange(view, anchorIso);
  const s = date(days[0]); const e = date(days[days.length - 1]);
  return s.hasSame(e, 'month') ? `${s.toFormat('d')}-${e.toFormat('d LLL yyyy')}` : s.hasSame(e, 'year') ? `${s.toFormat('d LLL')} - ${e.toFormat('d LLL yyyy')}` : `${s.toFormat('d LLL yyyy')} - ${e.toFormat('d LLL yyyy')}`;
}

/** Local dates an item covers (all-day end is exclusive; a timed item ending at midnight doesn't spill over). */
export function itemDays(it: CalendarItem): string[] {
  let s: DateTime; let e: DateTime;
  if (it.all_day) { s = date(it.start); e = date(it.end).minus({ days: 1 }); }
  else { s = local(it.start).startOf('day'); e = local(it.end).minus({ minutes: 1 }).startOf('day'); if (e < s) e = s; }
  const out: string[] = [];
  for (let d = s; d <= e && out.length < 62; d = d.plus({ days: 1 })) out.push(d.toISODate()!);
  return out;
}

export function itemsByDay(items: CalendarItem[], days: string[]): Map<string, CalendarItem[]> {
  const m = new Map<string, CalendarItem[]>(days.map(d => [d, []]));
  for (const it of items) for (const d of itemDays(it)) m.get(d)?.push(it);
  // All-day first, then by start time.
  for (const list of m.values()) list.sort((a, b) => Number(b.all_day) - Number(a.all_day) || a.start.localeCompare(b.start) || a.title.localeCompare(b.title));
  return m;
}

export interface Placed { item: CalendarItem; top: number; height: number; col: number; cols: number }

/** Lay out timed items on one day (minutes from local midnight), splitting overlapping ones into columns. */
export function layoutDay(items: CalendarItem[], day: string): Placed[] {
  const d0 = date(day).startOf('day');
  const timed = items.filter(i => !i.all_day).map(item => {
    const s = Math.max(0, local(item.start).diff(d0, 'minutes').minutes);
    const e = Math.min(24 * 60, local(item.end).diff(d0, 'minutes').minutes);
    return { item, top: s, height: Math.max(e - s, 20) };
  }).sort((a, b) => a.top - b.top || b.height - a.height);
  const out: Placed[] = [];
  let group: Placed[] = []; let groupEnd = -1;
  const flush = () => { const cols = Math.max(...group.map(g => g.col)) + 1; for (const g of group) g.cols = cols; out.push(...group); group = []; };
  for (const t of timed) {
    if (group.length && t.top >= groupEnd) flush();
    const used = new Set(group.filter(g => g.top + g.height > t.top).map(g => g.col));
    let col = 0; while (used.has(col)) col++;
    group.push({ ...t, col, cols: 1 });
    groupEnd = Math.max(groupEnd, t.top + t.height);
  }
  if (group.length) flush();
  return out;
}

export function timeLabel(it: CalendarItem): string {
  if (it.all_day) return 'All day';
  const s = local(it.start); const e = local(it.end);
  return s.hasSame(e, 'day') ? `${clock(s)}-${clock(e)}` : `${s.toFormat('d LLL')}, ${clock(s)} - ${e.toFormat('d LLL')}, ${clock(e)}`;
}

export const REMINDER_CHOICES: Array<{ minutes: number; label: string; allDay?: boolean }> = [
  { minutes: 0, label: 'At the start' }, { minutes: 5, label: '5 minutes before' }, { minutes: 10, label: '10 minutes before' }, { minutes: 15, label: '15 minutes before' },
  { minutes: 30, label: '30 minutes before' }, { minutes: 60, label: '1 hour before' }, { minutes: 120, label: '2 hours before' }, { minutes: 1440, label: '1 day before' },
  { minutes: 2880, label: '2 days before' }, { minutes: 10080, label: '1 week before' },
];

/** Parse a custom reminder like "90m", "3h", "2d", "1w" (or a plain number of minutes). */
export function parseReminder(s: string): number | null {
  const m = s.trim().toLowerCase().match(/^(\d+)\s*(m|min|mins|minutes?|h|hrs?|hours?|d|days?|w|weeks?)?$/);
  if (!m) return null;
  const n = Number(m[1]); const u = (m[2] ?? 'm')[0];
  const v = u === 'h' ? n * 60 : u === 'd' ? n * 1440 : u === 'w' ? n * 10080 : n;
  return v >= 0 && v <= 7 * 1440 ? v : null;
}

export type RepeatChoice = 'none' | 'daily' | 'weekdays' | 'weekly' | 'fortnightly' | 'monthly' | 'yearly' | 'custom';

/** Map the simple repeat menu to a recurrence rule. */
export function repeatToRule(choice: RepeatChoice, startLocal: string, end: { until?: string | null; count?: number | null }, custom?: CalendarRecurrence): CalendarRecurrence | null {
  const tail = { ...(end.until ? { until: end.until } : {}), ...(end.count ? { count: end.count } : {}) };
  const isoDow = DateTime.fromISO(startLocal).weekday;
  switch (choice) {
    case 'none': return null;
    case 'daily': return { freq: 'daily', interval: 1, ...tail };
    case 'weekdays': return { freq: 'weekly', interval: 1, byweekday: [1, 2, 3, 4, 5], ...tail };
    case 'weekly': return { freq: 'weekly', interval: 1, byweekday: [isoDow], ...tail };
    case 'fortnightly': return { freq: 'weekly', interval: 2, byweekday: [isoDow], ...tail };
    case 'monthly': return { freq: 'monthly', interval: 1, ...tail };
    case 'yearly': return { freq: 'yearly', interval: 1, ...tail };
    case 'custom': return custom ? { ...custom, ...tail } : null;
  }
}

export function ruleToRepeat(r: CalendarRecurrence | null, startLocal: string): RepeatChoice {
  if (!r) return 'none';
  const dow = DateTime.fromISO(startLocal).weekday;
  const days = r.byweekday ?? [];
  if (r.freq === 'daily' && r.interval === 1) return 'daily';
  if (r.freq === 'weekly' && r.interval === 1 && days.join() === '1,2,3,4,5') return 'weekdays';
  if (r.freq === 'weekly' && (days.length === 0 || (days.length === 1 && days[0] === dow))) return r.interval === 1 ? 'weekly' : r.interval === 2 ? 'fortnightly' : 'custom';
  if (r.freq === 'monthly' && r.interval === 1) return 'monthly';
  if (r.freq === 'yearly' && r.interval === 1) return 'yearly';
  return 'custom';
}

/** Start / end fields of the event editor (dates yyyy-mm-dd, times HH:mm, UK time). */
export type When = { sDate: string; sTime: string; eDate: string; eTime: string };
/** Apply an edit and keep the end after the start: moving the start keeps the length; an end at or before the start becomes start + 1 hour (all day: the same day). */
export function keepEndAfter(prev: When, patch: Partial<When>, allDay: boolean): When {
  const next = { ...prev, ...patch };
  const at = (d: string, t: string) => DateTime.fromISO(`${d}T${allDay ? '00:00' : t || '00:00'}`, { zone: 'Europe/London' });
  const movedStart = patch.sDate !== undefined || patch.sTime !== undefined;
  if (!next.sDate) return next;
  const s0 = at(prev.sDate, prev.sTime), e0 = at(prev.eDate || prev.sDate, prev.eTime);
  const s1 = at(next.sDate, next.sTime);
  let e1 = at(next.eDate || next.sDate, next.eTime);
  if (movedStart && s0.isValid && e0.isValid) {
    const len = e0.diff(s0).as('minutes');
    e1 = s1.plus({ minutes: len > 0 || (allDay && len >= 0) ? len : 60 });
  }
  if (!e1.isValid || (allDay ? e1 < s1 : e1 <= s1)) e1 = allDay ? s1 : s1.plus({ hours: 1 });
  return { ...next, eDate: e1.toISODate()!, eTime: allDay ? next.eTime : e1.toFormat('HH:mm') };
}

/** t681u: who an invite reaches (now shared with the phone, t705u). */
export { expandInvitees, type InviteeChoice, type InviteeStaff } from '@tradeoutlet/shared';
